import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import type { Listing } from "@/types";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { EmptyState } from "@/components/ui/EmptyState";
import { Badge } from "@/components/ui/Badge";
import { ListingPlaceholder } from "@/components/ui/ListingPlaceholder";

const MY_LISTINGS_QUERY = gql`
  query MyListings($status: String) {
    myListings(status: $status) {
      id
      price
      condition
      conditionLabel
      status
      location
      createdAt
      displayTitle
      displayImageUrl
      game {
        id
      }
    }
  }
`;

const UPDATE_LISTING_STATUS_MUTATION = gql`
  mutation UpdateListingStatus($id: ID!, $status: String!) {
    updateListing(id: $id, status: $status) {
      listing {
        id
        status
      }
      errors
    }
  }
`;

const TABS = [
  { key: "all", label: "Visi" },
  { key: "active", label: "Aktyvūs" },
  { key: "reserved", label: "Rezervuoti" },
  { key: "sold", label: "Parduoti" },
  { key: "cancelled", label: "Atšaukti" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const STATUS_BADGE_VARIANT: Record<string, "success" | "warning" | "danger" | "info" | "default"> = {
  active: "success",
  reserved: "warning",
  sold: "info",
  cancelled: "danger",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Aktyvus",
  reserved: "Rezervuotas",
  sold: "Parduotas",
  cancelled: "Atšauktas",
};

interface StatusAction {
  label: string;
  status: string;
  className: string;
}

const STATUS_ACTIONS: Record<string, StatusAction[]> = {
  active: [
    { label: "Rezervuoti", status: "reserved", className: "text-amber-700 hover:bg-amber-50" },
    { label: "Pažymėti parduotu", status: "sold", className: "text-blue-700 hover:bg-blue-50" },
    { label: "Atšaukti", status: "cancelled", className: "text-red-700 hover:bg-red-50" },
  ],
  reserved: [
    { label: "Aktyvinti", status: "active", className: "text-green-700 hover:bg-green-50" },
    { label: "Pažymėti parduotu", status: "sold", className: "text-blue-700 hover:bg-blue-50" },
    { label: "Atšaukti", status: "cancelled", className: "text-red-700 hover:bg-red-50" },
  ],
  sold: [
    { label: "Aktyvinti", status: "active", className: "text-green-700 hover:bg-green-50" },
  ],
  cancelled: [
    { label: "Aktyvinti", status: "active", className: "text-green-700 hover:bg-green-50" },
  ],
};

function ListingActions({
  listing,
  onStatusChange,
  isPending,
}: {
  listing: Listing;
  onStatusChange: (id: string, status: string) => void;
  isPending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const actions = STATUS_ACTIONS[listing.status] || [];

  return (
    <div className="relative shrink-0" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        aria-label="Veiksmai"
      >
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {listing.status === "active" && (
            <Link
              to={`/listings/${listing.id}/edit`}
              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setOpen(false)}
            >
              Redaguoti
            </Link>
          )}

          {actions.map((action) => (
            <button
              key={action.status}
              onClick={() => {
                onStatusChange(listing.id, action.status);
                setOpen(false);
              }}
              disabled={isPending}
              className={`block w-full px-4 py-2 text-left text-sm disabled:opacity-50 ${action.className}`}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MyListingsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["myListings", activeTab],
    queryFn: () =>
      graphqlClient.request<{ myListings: Listing[] }>(MY_LISTINGS_QUERY, {
        status: activeTab === "all" ? null : activeTab,
      }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: (vars: { id: string; status: string }) =>
      graphqlClient.request<{ updateListing: { listing: { id: string; status: string } | null; errors: string[] } }>(
        UPDATE_LISTING_STATUS_MUTATION,
        vars
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myListings"] });
    },
  });

  const listings = data?.myListings || [];

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Mano skelbimai</h1>
        <div className="flex items-center gap-3">
          <Link
            to="/my-collections"
            className="text-sm font-medium text-primary-800 hover:text-primary-700"
          >
            Mano kolekcijos
          </Link>
          <Link to="/listings/new" className="btn-primary">
            Naujas skelbimas
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "border-primary-800 text-primary-800"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : listings.length === 0 ? (
        <EmptyState
          title="Skelbimų nerasta"
          description={
            activeTab === "active" || activeTab === "all"
              ? "Neturite skelbimų. Sukurkite naują!"
              : undefined
          }
          action={
            activeTab === "active" || activeTab === "all" ? (
              <Link to="/listings/new" className="btn-primary">
                Sukurti skelbimą
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-4">
          {listings.map((listing) => (
            <div
              key={listing.id}
              className="card flex items-center gap-4 p-4"
            >
              <Link to={listing.game ? `/games/${listing.game.id}` : `/listings/${listing.id}`} className="shrink-0">
                {listing.displayImageUrl ? (
                  <img
                    src={listing.displayImageUrl}
                    alt={listing.displayTitle}
                    className="h-16 w-16 rounded-lg object-cover"
                  />
                ) : (
                  <ListingPlaceholder className="h-16 w-16 rounded-lg" />
                )}
              </Link>

              <div className="flex-1 min-w-0">
                <Link
                  to={`/listings/${listing.id}`}
                  className="text-sm font-semibold text-gray-900 hover:text-primary-800"
                >
                  {listing.displayTitle}
                </Link>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {listing.price != null && (
                    <span className="text-sm font-bold text-primary-800">
                      {listing.price.toFixed(2)} &euro;
                    </span>
                  )}
                  <Badge variant={STATUS_BADGE_VARIANT[listing.status] || "default"}>
                    {STATUS_LABELS[listing.status] || listing.status}
                  </Badge>
                  <span className="text-xs text-gray-500">
                    {listing.conditionLabel}
                  </span>
                </div>
              </div>

              <ListingActions
                listing={listing}
                onStatusChange={(id, status) => updateStatusMutation.mutate({ id, status })}
                isPending={updateStatusMutation.isPending}
              />
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
